/**
 * Contour Board — the workflow-context surface for one contour (Topic 07, spec
 * `specs/v3/screens/07-contour-graph-board.html`).
 *
 * A contour's phases become COLUMNS on a board. Each column shows the object
 * positions sitting in that phase, and any transition PROPOSALS targeting it
 * surface as proposed cards on the board (the kernel writes proposals; they are
 * never created here). Beside the board, three lenses are available:
 *
 *   • Board        — phases-as-lanes with positions + proposed transitions.
 *   • Transitions  — the guard/transition edge table (read of the process graph).
 *   • Object Table — every artifact position with its phase + scores.
 *
 * Doctrine: this is a READ projection of an authorized {@link ContourRuntimePacket}
 * served by `GET /api/processes/:id/runtime`. It renders state and links out; it
 * never mutates business truth. The eight canonical render states route through
 * the shared {@link RenderState} contract (S13). Contour instances and transition
 * proposals are PROPOSED modules — surfaced as such, never asserted as settled.
 */
import * as React from "react";
import { useParams } from "react-router-dom";
import { useGo } from "@refinedev/core";

import type { ContourRuntimePacket } from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Icon } from "@/components/ui/icon";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/app/StatusBadge";
import { RenderState, type RenderStateKind } from "@/components/app/RenderState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Field, FieldGrid, InspectorSection, RefLink } from "@/components/inspector";
import { api } from "@/lib/api";
import { formatDateTime, humanize } from "@/lib/format";

/** The three read lenses the board offers over the same runtime packet. */
type ContourLens = "board" | "transitions" | "objects";

/**
 * Element types derived from the packet — `@aos/shared` exports the packet type
 * and the element schemas, but not these row types by name, so we read them off
 * the packet shape to stay in lockstep with the contract.
 */
type ContourRuntimeLane = ContourRuntimePacket["lanes"][number];
type ContourRuntimePosition = ContourRuntimePacket["positions"][number];
type ContourRuntimeProposal = ContourRuntimePacket["proposals"][number];
type ContourRuntimeTransition = ContourRuntimePacket["graph"]["edges"][number];

/** Load the read-only contour runtime packet (mirrors useDecisionRoomPacket). */
function useContourRuntimePacket(contourId: string) {
  const [data, setData] = React.useState<ContourRuntimePacket | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const reload = React.useCallback(() => setReloadKey((value) => value + 1), []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<ContourRuntimePacket>(`/processes/${encodeURIComponent(contourId)}/runtime`)
      .then((packet) => {
        if (!cancelled) setData(packet);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load Contour Board.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [contourId, reloadKey]);

  return { data, loading, error, reload };
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <p className="font-mono text-sm tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

/** A single object position rendered as a card inside a phase lane. */
function PositionCard({ position }: { position: ContourRuntimePosition }) {
  return (
    <Card data-testid="contour-position" className="border-border/70">
      <CardContent className="flex flex-col gap-1.5 p-3">
        <div className="flex items-start justify-between gap-2">
          <RefLink resource="artifacts" id={position.artifact_id} />
          {position.is_primary === 1 ? (
            <Badge variant="secondary">primary</Badge>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-1.5 text-xs text-muted-foreground">
          <Badge variant="outline">
            membership {position.membership_score.toFixed(2)}
          </Badge>
          {position.confidence_score === null ? null : (
            <Badge variant="outline">
              confidence {position.confidence_score.toFixed(2)}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * A proposed transition rendered as a card on the board. Proposals are a
 * PROPOSED module: they read distinctly (dashed) and link to the gating decision
 * — they never imply the move has happened.
 */
function ProposalCard({ proposal }: { proposal: ContourRuntimeProposal }) {
  return (
    <Card data-testid="contour-proposal" className="border-dashed border-foreground/40">
      <CardContent className="flex flex-col gap-1.5 p-3">
        <div className="flex items-start justify-between gap-2">
          <span className="flex items-center gap-1.5 text-sm font-medium">
            <Icon name="transitionProposals" size={14} aria-hidden />
            Proposed move
          </span>
          <StatusBadge status={proposal.status} />
        </div>
        <p className="text-xs text-muted-foreground">
          <RefLink resource="artifacts" id={proposal.artifact_id} />{" "}
          {proposal.from_phase_label ?? "—"} → {proposal.to_phase_label}
        </p>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <Badge variant="outline">{humanize(proposal.proposer_type)}</Badge>
          {proposal.decision_id ? (
            <RefLink resource="decisions" id={proposal.decision_id} label="Gating decision" />
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

/** One phase column on the board: positions in the phase + proposals targeting it. */
function PhaseLane({
  lane,
  positionsById,
  proposalsById,
}: {
  lane: ContourRuntimeLane;
  positionsById: ReadonlyMap<string, ContourRuntimePosition>;
  proposalsById: ReadonlyMap<string, ContourRuntimeProposal>;
}) {
  const positions = lane.position_ids
    .map((id) => positionsById.get(id))
    .filter((value): value is ContourRuntimePosition => value !== undefined);
  const proposals = lane.proposal_ids
    .map((id) => proposalsById.get(id))
    .filter((value): value is ContourRuntimeProposal => value !== undefined);
  const total = positions.length + proposals.length;

  return (
    <div
      data-testid="contour-lane"
      className="flex min-h-[18rem] w-72 shrink-0 flex-col rounded-md border border-border bg-card"
    >
      <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/40 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-semibold">{lane.label}</span>
          {lane.is_start ? <Badge variant="outline">start</Badge> : null}
          {lane.is_terminal ? <Badge variant="outline">terminal</Badge> : null}
          {lane.kind === "mode" ? <Badge variant="secondary">mode</Badge> : null}
        </div>
        <span className="font-mono text-xs text-muted-foreground tabular-nums">{total}</span>
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3">
        {total === 0 ? (
          <p className="text-xs text-muted-foreground">No objects in this phase.</p>
        ) : (
          <>
            {positions.map((position) => (
              <PositionCard key={position.id} position={position} />
            ))}
            {proposals.map((proposal) => (
              <ProposalCard key={proposal.id} proposal={proposal} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

/** The board lens: phases-as-lanes, horizontally scrollable. */
function BoardLens({ packet }: { packet: ContourRuntimePacket }) {
  const positionsById = React.useMemo(
    () => new Map(packet.positions.map((position) => [position.id, position])),
    [packet.positions],
  );
  const proposalsById = React.useMemo(
    () => new Map(packet.proposals.map((proposal) => [proposal.id, proposal])),
    [packet.proposals],
  );

  if (packet.lanes.length === 0) {
    return (
      <RenderState
        state="empty"
        empty={{
          icon: "processes",
          title: "This contour has no phases yet.",
          hint: "Add phases to the contour to see objects flow across the board.",
        }}
      />
    );
  }

  return (
    <ScrollArea className="w-full whitespace-nowrap rounded-md border border-border">
      <div className="flex gap-3 p-3">
        {packet.lanes.map((lane) => (
          <PhaseLane
            key={lane.id}
            lane={lane}
            positionsById={positionsById}
            proposalsById={proposalsById}
          />
        ))}
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
}

/** The transitions lens: the guard/transition edges of the process graph. */
function TransitionsLens({ edges }: { edges: readonly ContourRuntimeTransition[] }) {
  if (edges.length === 0) {
    return (
      <RenderState
        state="empty"
        empty={{
          icon: "graph",
          title: "No transitions defined.",
          hint: "Transition guards appear once the contour graph has edges.",
        }}
      />
    );
  }
  return (
    <div className="rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Transition</TableHead>
            <TableHead>Guard</TableHead>
            <TableHead>Rule</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {edges.map((edge) => (
            <TableRow key={edge.id} data-testid="contour-transition">
              <TableCell className="font-medium">
                {edge.source} → {edge.target}
              </TableCell>
              <TableCell>{edge.label}</TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground">
                {edge.rule_id}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** The object-table lens: every position with its phase + membership scores. */
function ObjectTableLens({
  positions,
}: {
  positions: readonly ContourRuntimePosition[];
}) {
  if (positions.length === 0) {
    return (
      <RenderState
        state="empty"
        empty={{
          icon: "artifacts",
          title: "No objects positioned in this contour.",
          hint: "Objects appear here once they enter a phase.",
        }}
      />
    );
  }
  return (
    <div className="rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Object</TableHead>
            <TableHead>Phase</TableHead>
            <TableHead>Membership</TableHead>
            <TableHead>Confidence</TableHead>
            <TableHead>Updated</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {positions.map((position) => (
            <TableRow key={position.id} data-testid="contour-object-row">
              <TableCell>
                <RefLink resource="artifacts" id={position.artifact_id} />
              </TableCell>
              <TableCell>{position.phase_label}</TableCell>
              <TableCell className="font-mono text-xs tabular-nums">
                {position.membership_score.toFixed(2)}
              </TableCell>
              <TableCell className="font-mono text-xs tabular-nums">
                {position.confidence_score === null
                  ? "—"
                  : position.confidence_score.toFixed(2)}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {formatDateTime(position.updated_at)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ContourBoardContent({
  packet,
  reload,
}: {
  packet: ContourRuntimePacket;
  reload: () => void;
}) {
  const [lens, setLens] = React.useState<ContourLens>("board");
  const summary = packet.summary;
  const policy = packet.policy;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        <Metric label="Phases" value={summary.phase_count} />
        <Metric label="Transitions" value={summary.transition_count} />
        <Metric label="Active instances" value={summary.active_instances} />
        <Metric label="Positioned" value={summary.positioned_artifacts} />
        <Metric label="Pending proposals" value={summary.pending_proposals} />
        <Metric label="Modes" value={summary.mode_count} />
      </div>

      <InspectorSection
        title="Policy"
        aside={
          <span className="text-xs text-muted-foreground">
            {humanize(policy.runtime_mode)} runtime
          </span>
        }
      >
        <FieldGrid>
          <Field label="Contour">{policy.contour_name}</Field>
          <Field label="Object type">
            {policy.object_type ? humanize(policy.object_type) : "—"}
          </Field>
          <Field label="Runtime mode">{humanize(policy.runtime_mode)}</Field>
          <Field label="Rigidity">{policy.rigidity_level}</Field>
          <Field label="Auto transition">{policy.auto_transition === 1 ? "On" : "Off"}</Field>
          <Field label="Unknown transition">{humanize(policy.unknown_transition)}</Field>
        </FieldGrid>
        {policy.modes.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {policy.modes.map((mode) => (
              <Badge key={mode} variant="secondary">
                {mode}
              </Badge>
            ))}
          </div>
        ) : null}
      </InspectorSection>

      <InspectorSection
        title="Board"
        aside={
          <div className="flex items-center gap-3">
            <Tabs value={lens} onValueChange={(value) => setLens(value as ContourLens)}>
              <TabsList>
                <TabsTrigger value="board" data-testid="lens-board">
                  Board
                </TabsTrigger>
                <TabsTrigger value="transitions" data-testid="lens-transitions">
                  Transitions
                </TabsTrigger>
                <TabsTrigger value="objects" data-testid="lens-objects">
                  Object Table
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <Button variant="outline" size="sm" onClick={reload}>
              <Icon name="reopen" data-icon="inline-start" aria-hidden />
              Refresh
            </Button>
          </div>
        }
      >
        {lens === "board" ? <BoardLens packet={packet} /> : null}
        {lens === "transitions" ? <TransitionsLens edges={packet.graph.edges} /> : null}
        {lens === "objects" ? <ObjectTableLens positions={packet.positions} /> : null}
      </InspectorSection>

      <InspectorSection title="Instances">
        {packet.instances.length === 0 ? (
          <RenderState
            state="empty"
            empty={{
              icon: "contourInstances",
              title: "No contour instances.",
              hint: "Contour instances are a proposed module; none have been opened.",
            }}
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {packet.instances.map((instance) => (
              <Card key={instance.id}>
                <CardHeader className="p-4 pb-2">
                  <div className="flex items-start justify-between gap-3">
                    <CardTitle className="text-base">
                      <RefLink resource="artifacts" id={instance.artifact_id} />
                    </CardTitle>
                    <StatusBadge status={instance.status} />
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <FieldGrid>
                    <Field label="Phase">{instance.current_phase_label ?? "—"}</Field>
                    <Field label="Started">{formatDateTime(instance.started_at)}</Field>
                  </FieldGrid>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </InspectorSection>
    </div>
  );
}

export function ContourBoardPage({ contourId }: { contourId: string }) {
  const go = useGo();
  const { data, loading, error, reload } = useContourRuntimePacket(contourId);

  const state: RenderStateKind = loading
    ? "loading"
    : error
      ? "error"
      : "normal";

  return (
    <div className="max-w-6xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Contour Board"
          subtitle="Workflow-context surface: phases as columns, object positions, proposed transitions, guards, and policy."
          icon="processes"
          actions={
            <Button
              variant="outline"
              onClick={() => go({ to: `/processes/${contourId}/graph` })}
            >
              <Icon name="graph" data-icon="inline-start" aria-hidden />
              Graph
            </Button>
          }
        />
        <RenderState
          state={state}
          loadingRows={6}
          error={error}
          errorMessage={error ?? undefined}
          onRetry={reload}
        >
          {data ? <ContourBoardContent packet={data} reload={reload} /> : null}
        </RenderState>
      </div>
    </div>
  );
}

export function ContourBoardRoute() {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return <RenderState state="error" errorMessage="Missing contour id." />;
  }
  return <ContourBoardPage contourId={id} />;
}

export default ContourBoardRoute;
